import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, BrainIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
const PredictiveMaintenanceDashboard: React.FC = () => {
  const kpis = [{
    title: 'Active Alerts',
    value: '47',
    change: '+12',
    isPositive: false
  }, {
    title: 'Early Failure Detection %',
    value: '84.3%',
    change: '+5.2%',
    isPositive: true
  }, {
    title: 'Average Prevention Savings',
    value: '₹2.8 L',
    change: '+₹0.4 L',
    isPositive: true
  }];
  const failureProbabilityTrendData = [{
    week: 'Week 1',
    critical: 5,
    high: 8,
    medium: 12,
    low: 18
  }, {
    week: 'Week 2',
    critical: 7,
    high: 10,
    medium: 15,
    low: 20
  }, {
    week: 'Week 3',
    critical: 6,
    high: 12,
    medium: 14,
    low: 22
  }, {
    week: 'Week 4',
    critical: 9,
    high: 11,
    medium: 16,
    low: 19
  }, {
    week: 'Week 5',
    critical: 8,
    high: 13,
    medium: 18,
    low: 21
  }, {
    week: 'Week 6',
    critical: 11,
    high: 14,
    medium: 17,
    low: 23
  }, {
    week: 'Week 7',
    critical: 10,
    high: 15,
    medium: 19,
    low: 24
  }, {
    week: 'Week 8',
    critical: 12,
    high: 16,
    medium: 20,
    low: 25
  }];
  const sensorAnomalyCategoriesData = [{
    category: 'Temperature Sensors',
    count: 142,
    percentage: 32.1,
    color: '#ef4444'
  }, {
    category: 'Vibration Sensors',
    count: 118,
    percentage: 26.7,
    color: '#f59e0b'
  }, {
    category: 'Pressure Sensors',
    count: 89,
    percentage: 20.1,
    color: '#3b82f6'
  }, {
    category: 'Flow Sensors',
    count: 56,
    percentage: 12.7,
    color: '#10b981'
  }, {
    category: 'Position Sensors',
    count: 37,
    percentage: 8.4,
    color: '#8b5cf6'
  }];
  const parameterDeviationData = [{
    parameter: 'Motor Temperature',
    normal: 75,
    current: 92,
    threshold: 85,
    deviation: 22.7,
    status: 'Critical'
  }, {
    parameter: 'Vibration Level',
    normal: 3.5,
    current: 5.8,
    threshold: 5.0,
    deviation: 65.7,
    status: 'High'
  }, {
    parameter: 'Oil Pressure',
    normal: 45,
    current: 38,
    threshold: 40,
    deviation: -15.6,
    status: 'Medium'
  }, {
    parameter: 'Bearing Temp',
    normal: 65,
    current: 78,
    threshold: 75,
    deviation: 20.0,
    status: 'High'
  }, {
    parameter: 'Coolant Flow',
    normal: 12,
    current: 9.5,
    threshold: 10,
    deviation: -20.8,
    status: 'Medium'
  }, {
    parameter: 'Belt Tension',
    normal: 100,
    current: 85,
    threshold: 90,
    deviation: -15.0,
    status: 'Medium'
  }];
  const aiSuggestedActions = [{
    priority: 'Critical',
    equipment: 'CNC Machine #3 - Motor Assembly',
    issue: 'Temperature exceeding safe limits by 22.7%',
    action: 'Schedule immediate inspection and cooling system check. Replace thermal paste and verify fan operation.',
    estimatedCost: '₹45,000',
    preventionSavings: '₹3.2 L',
    timeframe: 'Within 24 hours',
    confidence: 94
  }, {
    priority: 'High',
    equipment: 'Hydraulic Press #7 - Vibration Sensor',
    issue: 'Abnormal vibration pattern detected, 65.7% above baseline',
    action: 'Perform bearing inspection and alignment check. Lubrication system may need servicing.',
    estimatedCost: '₹32,000',
    preventionSavings: '₹2.8 L',
    timeframe: '2-3 days',
    confidence: 89
  }, {
    priority: 'High',
    equipment: 'Assembly Line Robot #12 - Bearing Temperature',
    issue: 'Bearing temperature rising, currently 20% above normal',
    action: 'Replace bearings during next scheduled maintenance window. Monitor closely until replacement.',
    estimatedCost: '₹28,000',
    preventionSavings: '₹2.1 L',
    timeframe: '3-5 days',
    confidence: 87
  }, {
    priority: 'Medium',
    equipment: 'Cooling System #5 - Oil Pressure',
    issue: 'Oil pressure 15.6% below optimal range',
    action: 'Check oil levels and filter condition. May require oil change or pump inspection.',
    estimatedCost: '₹18,000',
    preventionSavings: '₹1.5 L',
    timeframe: '1 week',
    confidence: 82
  }, {
    priority: 'Medium',
    equipment: 'Conveyor Belt System #2 - Coolant Flow',
    issue: 'Coolant flow rate decreased by 20.8%',
    action: 'Inspect coolant pump and check for blockages in cooling lines. Clean or replace filters.',
    estimatedCost: '₹15,000',
    preventionSavings: '₹1.2 L',
    timeframe: '1 week',
    confidence: 79
  }];
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return 'text-red-600 bg-red-50 border-red-300';
      case 'High':
        return 'text-orange-600 bg-orange-50 border-orange-300';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-300';
      default:
        return 'text-green-600 bg-green-50 border-green-300';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critical':
        return 'bg-red-500';
      case 'High':
        return 'bg-orange-500';
      case 'Medium':
        return 'bg-yellow-500';
      default:
        return 'bg-green-500';
    }
  };
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600 font-bold';
    if (confidence >= 80) return 'text-blue-600 font-semibold';
    return 'text-yellow-600 font-semibold';
  };
  return <div data-id="element-300">
      <DescriptionPanel title="Predictive Maintenance & AI Insights" description="AI-powered predictive maintenance system monitoring equipment health, sensor anomalies, and parameter deviations. Leverages machine learning to detect early failure patterns and recommend proactive maintenance actions to prevent costly breakdowns." insights={['47 active alerts requiring attention - 12 new alerts in last 24 hours', 'Early failure detection at 84.3% - preventing average ₹2.8L in breakdown costs', 'Motor temperature sensors showing highest anomaly rate at 32.1% of total alerts', 'AI confidence level averaging 86.2% across all maintenance recommendations']} data-id="element-301" />
      {/* KPI Tiles */}
      <div className="mb-8" data-id="element-302">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-303">
          Predictive Maintenance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-304">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-305">
              <div className="flex flex-col h-full" data-id="element-306">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-307">
                  {kpi.title}
                </div>
                <div className="mt-3 text-4xl font-bold text-gray-900" data-id="element-308">
                  {kpi.value}
                </div>
                <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-309">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-310" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-311" />}
                  {kpi.change} vs last week
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Failure Probability Trend */}
      <Card className="p-6 mb-8" data-id="element-312">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-313">
          Failure Probability Trend (8 Weeks)
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-314">
          Equipment failure risk distribution by severity level
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-315">
          <AreaChart data={failureProbabilityTrendData} data-id="element-316">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-317" />
            <XAxis dataKey="week" tick={{
            fontSize: 12
          }} data-id="element-318" />
            <YAxis tick={{
            fontSize: 12
          }} label={{
            value: 'Number of Alerts',
            angle: -90,
            position: 'insideLeft'
          }} data-id="element-319" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-320" />
            <Legend data-id="element-321" />
            <Area type="monotone" dataKey="critical" stackId="1" stroke="#ef4444" fill="#ef4444" name="Critical Risk" data-id="element-322" />
            <Area type="monotone" dataKey="high" stackId="1" stroke="#f59e0b" fill="#f59e0b" name="High Risk" data-id="element-323" />
            <Area type="monotone" dataKey="medium" stackId="1" stroke="#eab308" fill="#eab308" name="Medium Risk" data-id="element-324" />
            <Area type="monotone" dataKey="low" stackId="1" stroke="#22c55e" fill="#22c55e" name="Low Risk" data-id="element-325" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-326">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-327">
            <div className="text-center" data-id="element-328">
              <div className="text-gray-600" data-id="element-329">Critical Alerts</div>
              <div className="text-xl font-bold text-red-600" data-id="element-330">12</div>
            </div>
            <div className="text-center" data-id="element-331">
              <div className="text-gray-600" data-id="element-332">High Risk</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-333">16</div>
            </div>
            <div className="text-center" data-id="element-334">
              <div className="text-gray-600" data-id="element-335">Medium Risk</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-336">20</div>
            </div>
            <div className="text-center" data-id="element-337">
              <div className="text-gray-600" data-id="element-338">Low Risk</div>
              <div className="text-xl font-bold text-green-600" data-id="element-339">25</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Sensor Anomaly Categories and Parameter Deviation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-340">
        <Card className="p-6" data-id="element-341">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-342">
            Sensor Anomaly Categories
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-343">
            Distribution of anomalies by sensor type
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-344">
            <PieChart data-id="element-345">
              <Pie data={sensorAnomalyCategoriesData} cx="50%" cy="50%" labelLine={false} label={({
              category,
              percentage
            }) => `${category.split(' ')[0]}: ${percentage}%`} outerRadius={100} fill="#8884d8" dataKey="percentage" data-id="element-346">
                {sensorAnomalyCategoriesData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-347" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number, name: string, props: any) => [`${props.payload.count} anomalies (${value}%)`, props.payload.category]} data-id="element-348" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-349">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-350">
              <div className="text-center" data-id="element-351">
                <div className="text-gray-600" data-id="element-352">Total Anomalies</div>
                <div className="text-xl font-bold text-gray-900" data-id="element-353">442</div>
              </div>
              <div className="text-center" data-id="element-354">
                <div className="text-gray-600" data-id="element-355">Most Common</div>
                <div className="text-sm font-bold text-red-600" data-id="element-356">
                  Temperature
                </div>
              </div>
              <div className="text-center" data-id="element-357">
                <div className="text-gray-600" data-id="element-358">Sensors Monitored</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-359">1,247</div>
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-6" data-id="element-360">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-361">
            Parameter Deviation Chart
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-362">
            Key parameters deviating from normal operating ranges
          </p>
          <div className="space-y-4" data-id="element-363">
            {parameterDeviationData.map((param, index) => <div key={index} className="space-y-2" data-id="element-364">
                <div className="flex items-center justify-between" data-id="element-365">
                  <div className="flex items-center" data-id="element-366">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(param.status)} mr-3`} data-id="element-367" />
                    <div data-id="element-368">
                      <div className="text-sm font-semibold text-gray-800" data-id="element-369">
                        {param.parameter}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-370">
                        Normal: {param.normal} | Current: {param.current} |
                        Threshold: {param.threshold}
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-371">
                    <div className={`text-sm font-bold ${param.deviation > 0 ? 'text-red-600' : 'text-blue-600'}`} data-id="element-372">
                      {param.deviation > 0 ? '+' : ''}
                      {param.deviation.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="relative h-6 bg-gray-100 rounded-lg overflow-hidden" data-id="element-373">
                  <div className="absolute h-full bg-gray-300" style={{
                width: '50%',
                left: '0'
              }} data-id="element-374" />
                  <div className={`absolute h-full ${getStatusColor(param.status)}`} style={{
                width: `${Math.abs(param.deviation) / 2}%`,
                left: param.deviation > 0 ? '50%' : `${50 - Math.abs(param.deviation) / 2}%`
              }} data-id="element-375" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-376">
                    <span className="text-xs font-bold text-white" data-id="element-377">
                      {param.status} Deviation
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-378">
            <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-379">
              <span data-id="element-380">Status Levels:</span>
              <div className="flex items-center space-x-3" data-id="element-381">
                <div className="flex items-center" data-id="element-382">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-1" data-id="element-383" />
                  <span data-id="element-384">Critical</span>
                </div>
                <div className="flex items-center" data-id="element-385">
                  <div className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-386" />
                  <span data-id="element-387">High</span>
                </div>
                <div className="flex items-center" data-id="element-388">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-389" />
                  <span data-id="element-390">Medium</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* AI Suggested Maintenance Actions Panel */}
      <Card className="p-6" data-id="element-391">
        <div className="flex items-center justify-between mb-4" data-id="element-392">
          <div className="flex items-center" data-id="element-393">
            <BrainIcon size={24} className="text-blue-600 mr-3" data-id="element-394" />
            <div data-id="element-395">
              <h3 className="text-lg font-semibold text-gray-800" data-id="element-396">
                AI-Suggested Maintenance Actions
              </h3>
              <p className="text-sm text-gray-600" data-id="element-397">
                Machine learning powered recommendations for proactive
                maintenance
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2" data-id="element-398">
            <AlertTriangleIcon size={20} className="text-orange-600" data-id="element-399" />
            <span className="text-sm font-semibold text-orange-600" data-id="element-400">
              {aiSuggestedActions.length} Actions Recommended
            </span>
          </div>
        </div>
        <div className="space-y-4" data-id="element-401">
          {aiSuggestedActions.map((action, index) => <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all" data-id="element-402">
              <div className="flex items-start justify-between mb-3" data-id="element-403">
                <div className="flex items-start flex-1" data-id="element-404">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold border mr-3 ${getPriorityColor(action.priority)}`} data-id="element-405">
                    {action.priority}
                  </span>
                  <div className="flex-1" data-id="element-406">
                    <h4 className="text-sm font-bold text-gray-900 mb-1" data-id="element-407">
                      {action.equipment}
                    </h4>
                    <p className="text-sm text-gray-700 mb-2" data-id="element-408">
                      <span className="font-semibold" data-id="element-409">Issue:</span>{' '}
                      {action.issue}
                    </p>
                  </div>
                </div>
                <div className="text-right ml-4" data-id="element-410">
                  <div className="text-xs text-gray-500" data-id="element-411">AI Confidence</div>
                  <div className={getConfidenceColor(action.confidence)} data-id="element-412">
                    {action.confidence}%
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3" data-id="element-413">
                <div className="text-xs font-semibold text-blue-800 mb-1" data-id="element-414">
                  Recommended Action:
                </div>
                <div className="text-sm text-blue-900" data-id="element-415">{action.action}</div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-416">
                <div data-id="element-417">
                  <div className="text-xs text-gray-500" data-id="element-418">Estimated Cost</div>
                  <div className="font-bold text-gray-900" data-id="element-419">
                    {action.estimatedCost}
                  </div>
                </div>
                <div data-id="element-420">
                  <div className="text-xs text-gray-500" data-id="element-421">
                    Prevention Savings
                  </div>
                  <div className="font-bold text-green-600" data-id="element-422">
                    {action.preventionSavings}
                  </div>
                </div>
                <div data-id="element-423">
                  <div className="text-xs text-gray-500" data-id="element-424">Timeframe</div>
                  <div className="font-semibold text-orange-600" data-id="element-425">
                    {action.timeframe}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-426">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-427">
            <div className="text-center" data-id="element-428">
              <div className="text-gray-600" data-id="element-429">Total Actions</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-430">
                {aiSuggestedActions.length}
              </div>
            </div>
            <div className="text-center" data-id="element-431">
              <div className="text-gray-600" data-id="element-432">Total Est. Cost</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-433">₹1.38 L</div>
            </div>
            <div className="text-center" data-id="element-434">
              <div className="text-gray-600" data-id="element-435">Potential Savings</div>
              <div className="text-xl font-bold text-green-600" data-id="element-436">₹10.8 L</div>
            </div>
            <div className="text-center" data-id="element-437">
              <div className="text-gray-600" data-id="element-438">Avg AI Confidence</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-439">86.2%</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default PredictiveMaintenanceDashboard;